<template>
  <div v-if="shouldShowNotes">
    <div class="notes-card">
      <div class="notes-header">
        <b-icon icon="message-text" size="is-small" class="notes-icon"></b-icon>
        <span class="notes-title">{{ notesTitle }}</span>
      </div>

      <div
        class="notes-text"
        :class="{ 'notes-text-collapsed': !isExpanded && hasLongContent }"
        ref="notesText"
      >
        <div v-if="notesContent" v-html="notesContent"></div>
        <div v-else class="has-text-grey">
          <p>No announcements at this time.</p>
        </div>
      </div>
      <div>
        <button
          v-if="showExpandButton && hasLongContent"
          @click="toggleExpanded"
          class="notes-toggle mt-3"
        >
          <b-icon
            :icon="isExpanded ? 'chevron-up' : 'chevron-down'"
            size="is-small"
          ></b-icon>
          <span v-if="isExpanded">Show less</span>
          <span v-else>Show more</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Notes',
  props: {
    content: {
      type: String,
      default: ''
    },
    maxHeight: {
      type: String,
      default: '50px'
    },
    section: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: 'Company Notes'
    }
  },
  data() {
    return {
      isExpanded: false,
      showExpandButton: false,
      hasLongContent: false
    }
  },
  computed: {
    currentSection() {
      // Use provided section or auto-detect based on route
      if (this.section) {
        return this.section
      }

      const routePath = this.$route.path
      if (routePath === '/home') return 'home'
      if (routePath.includes('/search/flights')) return 'airSearch'
      if (routePath.includes('/search/hotels')) return 'hotelSearch'
      if (routePath.includes('/search/cars')) return 'carSearch'
      if (routePath.includes('/search/checkout')) return 'checkout'

      return 'home'
    },

    // Get notes from store cache
    cachedNote() {
      return this.$store.getters['travellersAndTrips/getActiveAccountNoteBySection'](this.currentSection)
    },

    notesContent() {
      // Use provided content prop, cached note, or default content
      if (this.content) {
        return this.content
      }

      if (this.cachedNote && this.cachedNote.content) {
        return this.cachedNote.content
      }

      return ''
    },

    shouldShowNotes() {
      return this.notesContent && this.notesContent.trim().length > 0
    },

    notesTitle() {
      return this.title
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.checkIfExpandNeeded()
    })
  },
  watch: {
    notesContent() {
      this.$nextTick(() => {
        this.checkIfExpandNeeded()
      })
    }
  },
  methods: {
    toggleExpanded() {
      this.isExpanded = !this.isExpanded
    },
    checkIfExpandNeeded() {
      if (this.$refs.notesText) {
        const element = this.$refs.notesText
        const maxHeight = parseInt(this.maxHeight)
        // Add a small delay to ensure DOM is fully rendered
        setTimeout(() => {
          this.hasLongContent = element.scrollHeight > maxHeight
          this.showExpandButton = this.hasLongContent
        }, 100)
      }
    }
  }
}
</script>

<style scoped>
.notes-card {
  background-color: white;
  color: #4a4a4a;
  display: block;
  padding: 0.75rem 1rem;
  /* border: 10px solid #E6E6E6; */ 
  border: 10px solid #ffffff;
  border-radius: 8px;
  /* box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); */
}

.notes-header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  color: #363636;
}

.notes-icon {
  color: #3273dc;
  margin-right: 0.5rem;
}

.notes-title {
  flex: 1;
  font-size: 0.9rem;
  font-weight: 600;
  color: #363636;
}

.notes-toggle {
  background: none;
  border: none;
  color: #3273dc;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.notes-toggle:hover {
  background-color: rgba(50, 115, 220, 0.1);
}

.notes-text {
  transition: max-height 0.3s ease-in-out;
  overflow: hidden;
  line-height: 1.4;
  font-size: 0.85rem;
  color: #4a4a4a;
}

.notes-text-collapsed {
  max-height: 50px;
  position: relative;
}

.notes-text-collapsed::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(transparent, white);
  pointer-events: none;
}

.notes-text >>> p {
  margin: 0.25rem 0;
  font-size: 0.85rem;
}

.notes-text >>> strong {
  color: #363636;
  font-weight: 600;
}

/* Mobile responsiveness */
@media screen and (max-width: 768px) {  
  .notes-card {
    padding: 0.5rem 0.75rem;
  }
  
  .notes-header {
    margin-bottom: 0.25rem;
  }
  
  .notes-title {
    font-size: 0.85rem;
  }
  
  .notes-text {
    font-size: 0.8rem;
  }
  
  .notes-text-collapsed {
    max-height: 30px;
  }
}
</style>
